/**
 * Turn flow (Phase 40.2): 'perUnit' is the game as it has always played — each
 * unit aims and fires on its own turn. 'batched' ("Plan, then resolve") lets a
 * human team queue a shot per unit while their units' turns come up, and the
 * queued shots all fly, in order, the moment control would pass to another
 * team. Nothing about turn order or the action stream changes: a queued unit's
 * turn simply ends with an order instead of a shot, and the batch fires those
 * orders through the normal fire path before the next team starts.
 *
 * Pure helpers; main.ts owns the queue and the playback.
 */
import type { FireParams, PlayerState, TurnFlow, Vec3, PlannedMove, QueuedOrder } from '../types';
export type { PlannedMove, QueuedOrder };
import { SeededRandom } from './random';
import type { RoundOrder } from './roundSim';

export type { TurnFlow };

/** A unit's planned movement for the round (round flows only): a walk to a surface direction or a jump. */
/** Control is about to pass to `nextPlayer`: fire the batch first if it would leave the planning team. */
export function shouldResolveBatch(queue: readonly QueuedOrder[], nextPlayer: Pick<PlayerState, 'teamIndex' | 'isBot'> | undefined): boolean {
  if (queue.length === 0) return false;
  if (!nextPlayer) return true;
  return nextPlayer.isBot || nextPlayer.teamIndex !== queue[0].teamIndex;
}

/** The next order whose shooter is still alive (earlier shots in the batch can kill a teammate), and the rest. */
export function takeNextOrder(queue: readonly QueuedOrder[], players: readonly Pick<PlayerState, 'id' | 'alive'>[]): { order: QueuedOrder; rest: QueuedOrder[] } | null {
  for (let i = 0; i < queue.length; i++) {
    const shooter = players.find(p => p.id === queue[i].playerId);
    if (shooter && shooter.alive) return { order: queue[i], rest: queue.slice(i + 1) };
  }
  return null;
}

/** Fire-button label while planning: how many of the team's living human units have an order. */
export function queueLabel(queue: readonly QueuedOrder[], teamIndex: number, players: readonly Pick<PlayerState, 'teamIndex' | 'alive' | 'isBot'>[]): string {
  const total = players.filter(p => p.alive && !p.isBot && p.teamIndex === teamIndex).length;
  const queued = queue.filter(o => o.teamIndex === teamIndex).length;
  return `QUEUE SHOT (${queued}/${total})`;
}

/**
 * Everyone-plans rounds: the order the queue flies in. Planning order would favour
 * whoever plans first on same-step impacts, so each round draws a fresh initiative
 * order from the match seed and round number — deterministic on every peer.
 */
export function roundInitiativeOrder<T>(queue: readonly T[], seed: string, round: number): T[] {
  const out = queue.slice();
  const rng = new SeededRandom(`${seed}-round-${round}`);
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng.range(0, i + 1));
    const tmp = out[i]; out[i] = out[j]; out[j] = tmp;
  }
  return out;
}

/** True when the next unit to act is on a team none of the planners belong to — the plans must resolve first. */
export function controlLeavesPlanners(plannerTeams: Iterable<number>, nextPlayer: PlayerState | undefined): boolean {
  if (!nextPlayer) return true;
  let any = false;
  for (const t of plannerTeams) { any = true; if (t === nextPlayer.teamIndex) return false; }
  return any;
}

/** One round order per unit: its queued shot (if any) and its planned move (if any), shots' queue order first. */
export function buildRoundOrders(queue: readonly QueuedOrder[], moves: Iterable<PlannedMove>): RoundOrder[] {
  const out: RoundOrder[] = [];
  const byId = new Map<number, RoundOrder>();
  for (const q of queue) {
    const o: RoundOrder = { playerId: q.playerId, fire: { params: q.params, launch: q.launch } };
    out.push(o); byId.set(q.playerId, o);
  }
  for (const m of moves) {
    let o = byId.get(m.playerId);
    if (!o) { o = { playerId: m.playerId }; out.push(o); byId.set(m.playerId, o); }
    if (m.jump) o.jump = m.jump;
    else if (m.walkTo) o.walk = m.walkTo;
  }
  return out;
}
